import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, apiFetch } from "@/lib/api";
import { toLocalISODate } from "@/lib/dateUtils";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = toLocalISODate;

const presets = () => {
  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return {
    week: [iso(startOfWeek), iso(now)],
    month: [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)],
    year: [iso(new Date(now.getFullYear(), 0, 1)), iso(now)],
  };
};

const Section = ({ title, total, rows, numberLabel, cols }) => (
  <div className="border rounded-md overflow-hidden">
    <div className="flex justify-between items-center bg-gray-50 px-3 py-2 border-b">
      <span className="font-semibold text-sm">{title} ({rows.length})</span>
      <span className="font-bold text-sm">{money(total)}</span>
    </div>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400 px-3 py-2">No entries</p>
    ) : rows.map((r, i) => (
      <div key={i} className="grid grid-cols-12 px-3 py-1.5 text-xs border-t">
        <span className="col-span-2 text-gray-500">{r.date}</span>
        <span className="col-span-4">{r[numberLabel]}</span>
        <span className="col-span-3 text-gray-600">{cols(r)}</span>
        <span className="col-span-3 text-right">{money(r.tax)}</span>
      </div>
    ))}
  </div>
);

const GSTSummaryReport = ({ open, onOpenChange }) => {
  const p = presets();
  const [range, setRange] = useState(p.month);
  const [active, setActive] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (r) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/reports/gst-summary?start=${r[0]}&end=${r[1]}`);
      setData(await res.json());
    } catch (e) { toast.error("Failed to load GST summary"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(range); }, [open]);

  const applyPreset = (key) => { const pr = presets(); setActive(key); setRange(pr[key]); load(pr[key]); };
  const applyCustom = () => { setActive("custom"); load(range); };

  const printReport = () => {
    if (!data) return;
    const rowsHtml = (rows, numberLabel, partyLabel) => rows.map(r =>
      `<tr><td style="border:1px solid #ddd;padding:5px 8px">${r.date}</td><td style="border:1px solid #ddd;padding:5px 8px">${r[numberLabel] || ''}</td><td style="border:1px solid #ddd;padding:5px 8px">${r[partyLabel] || ''}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.taxable_value)}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.cgst)}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.sgst)}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.igst)}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right;font-weight:bold">${money(r.tax)}</td></tr>`
    ).join("");
    const head = `<tr style="background:#eef"><th style="border:1px solid #ddd;padding:5px 8px;text-align:left">Date</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:left">No.</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:left">Party</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:right">Taxable Value</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:right">CGST</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:right">SGST</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:right">IGST</th><th style="border:1px solid #ddd;padding:5px 8px;text-align:right">Tax</th></tr>`;
    const html = `<!doctype html><html><head><title>GST Summary</title><style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%;font-size:12px;margin-top:8px}h1{font-size:18px;margin:0}h2{font-size:14px;margin:18px 0 0}.muted{color:#666;font-size:12px}</style></head><body>
      <h1>GARAGE — GST Summary</h1><div class="muted">${data.start} to ${data.end}</div>
      <table style="margin-top:14px">
        <tr><td style="padding:8px;font-weight:bold;background:#dfe">Output Tax (charged to customers)</td><td style="padding:8px;text-align:right;font-weight:bold;background:#dfe">${money(data.output.total_tax)}</td></tr>
        <tr><td style="padding:8px;font-weight:bold;background:#fee">Input Tax (paid to suppliers, ITC)</td><td style="padding:8px;text-align:right;font-weight:bold;background:#fee">${money(data.input.total_tax)}</td></tr>
        <tr><td style="padding:10px;font-weight:bold;font-size:15px">NET GST ${data.net_payable >= 0 ? 'PAYABLE' : 'CREDIT CARRIED FORWARD'}</td><td style="padding:10px;text-align:right;font-weight:bold;font-size:15px;color:${data.net_payable >= 0 ? 'red' : 'green'}">${money(Math.abs(data.net_payable))}</td></tr>
      </table>
      <h2>Output Tax — Invoices Issued (${data.output.count})</h2>
      <table>${head}${rowsHtml(data.output.invoices, 'invoice_number', 'customer_name')}</table>
      <h2>Input Tax — Purchase Orders with GST Bill (${data.input.count})</h2>
      <table>${head}${rowsHtml(data.input.purchase_orders, 'po_number', 'supplier_name')}</table>
      </body></html>`;
    const w = window.open("", "_blank", "width=1000,height=700");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="gst-summary-dialog">
        <DialogHeader>
          <DialogTitle>GST Summary</DialogTitle>
          <DialogDescription>Output Tax (charged on issued invoices) vs Input Tax (paid on "GST Bill Available" purchase orders) for the period — net GST payable at a glance.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          {[["week", "This Week"], ["month", "This Month"], ["year", "This Year"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={active === k ? "default" : "outline"} onClick={() => applyPreset(k)} data-testid={`gst-summary-preset-${k}`}>{l}</Button>
          ))}
          <div className="flex items-end gap-1 ml-2">
            <div><Label className="text-xs">From</Label><Input type="date" className="h-8 w-36" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} data-testid="gst-summary-start" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" className="h-8 w-36" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} data-testid="gst-summary-end" /></div>
            <Button size="sm" variant="outline" onClick={applyCustom} data-testid="gst-summary-apply">Apply</Button>
          </div>
          <Button size="sm" onClick={printReport} disabled={!data} data-testid="gst-summary-print">Print / PDF</Button>
        </div>

        {loading || !data ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-3 text-center">
                <div className="text-xs text-gray-500">Output Tax (Charged)</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="gst-summary-output-total">{money(data.output.total_tax)}</div>
              </div>
              <div className="rounded-md border p-3 text-center">
                <div className="text-xs text-gray-500">Input Tax (Paid / ITC)</div>
                <div className="text-lg font-bold text-red-600" data-testid="gst-summary-input-total">{money(data.input.total_tax)}</div>
              </div>
              <div className="rounded-md border p-3 text-center">
                <div className="text-xs text-gray-500">Net GST {data.net_payable >= 0 ? "Payable" : "Credit Carried Forward"}</div>
                <div className={`text-lg font-bold ${data.net_payable >= 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="gst-summary-net-payable">{money(Math.abs(data.net_payable))}</div>
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="grid grid-cols-4 bg-gray-50 px-3 py-2 border-b text-xs font-semibold">
                <span>Tax Head</span>
                <span className="text-right">Output</span>
                <span className="text-right">Input</span>
                <span className="text-right">Net</span>
              </div>
              {[["CGST", "cgst", "net_cgst"], ["SGST", "sgst", "net_sgst"], ["IGST", "igst", "net_igst"]].map(([label, key, netKey]) => (
                <div key={key} className="grid grid-cols-4 px-3 py-1.5 text-xs border-t">
                  <span className="font-medium">{label}</span>
                  <span className="text-right">{money(data.output[key])}</span>
                  <span className="text-right">{money(data.input[key])}</span>
                  <span className={`text-right font-medium ${data[netKey] >= 0 ? "text-red-600" : "text-emerald-700"}`}>{money(data[netKey])}</span>
                </div>
              ))}
            </div>

            <Section title="Output Tax — Invoices Issued" total={data.output.total_tax} rows={data.output.invoices} numberLabel="invoice_number" cols={(r) => r.customer_name || ""} />
            <Section title="Input Tax — Purchase Orders with GST Bill" total={data.input.total_tax} rows={data.input.purchase_orders} numberLabel="po_number" cols={(r) => r.supplier_name || ""} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GSTSummaryReport;
